import { Refresh } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC, useCallback, useLayoutEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { Locker } from "shared/locker"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { RootState } from "src/app/store"
import { selectSaThingById, thingsActions } from "src/features/things/thingsSlice"
import useLockerDisplayValues from "src/hooks/useLockerDisplayValues"
import { v4 as uuid } from "uuid"
import { useCreateThingsMutation, useGetThingsQuery } from "../features/things/thingsApi"
import { useGetTenantByIdQuery } from "../features/things/thingsSaApi"
import SaLockerConsoleHeader from "./saLocker/SaLockerConsoleHeader"
import SaLockerDashboardView from "./saLocker/SaLockerDashboardView"
import SaLockerDeleteDialog from "./saLocker/SaLockerDeleteDialog"
import SaLockerDetailsPanel from "./saLocker/SaLockerDetailsPanel"
import SaLockerSection from "./saLocker/SaLockerSection"
import SaLockerSnapshotDrawer from "./saLocker/SaLockerSnapshotDrawer"
import { readLockerVitals } from "./saLocker/saLockerVitals"
import SaLayout from "./SaLayout"
import SaLockerForm from "./SaLockerForm"
import SaLockerMigrationOverride from "./SaLockerMigrationOverride"
import SaLockerServiceToggle from "./SaLockerServiceToggle"

/**
 * Super-admin page for a single locker.
 *
 * The two things an admin alternates between — editing configuration and looking
 * at the physical unit — sit side by side in equal columns, so neither is
 * subordinate and the machine stays on screen while its record is edited. The
 * dashboard is the read-only twin of what the tenant's operators see, which is
 * what makes a support call ("door 12 is stuck") resolvable from this page.
 */
export const SaLockerPage: FC = () => {
  const { t } = useTranslation()
  const { lockerId } = useParams<{ lockerId: string }>()
  const isCreating = lockerId === "new"

  const locker = useAppSelector((s: RootState) =>
    selectSaThingById(s, lockerId || ""),
  ) as Locker

  const lockerDisplay = useLockerDisplayValues(locker)

  const dispatch = useAppDispatch()

  // LOCKER-353: this page reads its locker back out of the SHARED `saList`, and
  // that array is filled by whatever `SaGet*` ran last. The migration lifecycle
  // panel now fetches lockers with `ap.noSstLocker` — a slim shape with no
  // `sstLocker.doors` and no `sstLocker.address_*` — and the `saList` reducer
  // appends then dedupes KEEPING THE FIRST occurrence
  // (`thingsSlice.ts:557-562`). So without this clear, arriving here from the
  // accordion's `viewLocker` link would leave the slim row in place and throw
  // away the full-fat row this page's own query just fetched: the vitals and the
  // snapshot table would render from one field, and `onRefresh` would post an
  // undefined `sstLockerId`.
  //
  // Clearing alone is not enough — if RTK served this query from cache, no
  // `matchFulfilled` fires and nothing would refill the array — hence
  // `refetchOnMountOrArgChange` below. Together they make the page's data its
  // own regardless of where the user came from. Every other SA page already
  // clears on mount; this one was the exception.
  // `useLayoutEffect`, not `useEffect`: a passive effect runs after paint, so
  // arriving here with a slim row still in `saList` would paint one frame from
  // that row — vitals over a missing `sstLocker.doors`, a two-field snapshot
  // table — and would mount then immediately unmount both child panels,
  // resetting their local state. Clearing before paint skips that churn.
  useLayoutEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  const { isLoading, isFetching, isError, refetch } = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetLockers,
        "ap.ids[0]": lockerId,
      },
    },
    { skip: isCreating, refetchOnMountOrArgChange: true },
  )

  const {
    data: tenantData,
    isLoading: isLoadingTenant,
    refetch: refetchTenant,
  } = useGetTenantByIdQuery(
    { id: locker?.tenantId || "" },
    { skip: !locker?.tenantId },
  )

  const _refetch = useCallback(() => {
    refetch()
    refetchTenant()
  }, [refetch, refetchTenant])

  const [createThings, { isLoading: isCreatingThings }] = useCreateThingsMutation()

  const isV2 = locker?.sstApiVersion === "v2"

  const vitals = useMemo(
    () => (locker ? readLockerVitals(locker, Date.now()) : undefined),
    [locker],
  )

  // ── SST Refresh ─────────────────────────────────────
  const onRefresh = useCallback(async () => {
    const sstLockerId = isV2 ? locker?.sst2Locker?.id : locker?.sstLocker?._id
    await createThings({
      body: {
        things: [
          {
            thingTypeMrn: ActionReqThingTypeMrn,
            executionMode: ActionReqExecutionModeEnum.sync,
            actionType: ActionReqTypeEnum.SstLockerRefresh,
            idempotencyKey: uuid(),
            delaySeconds: 2,
            tenantId: locker?.tenantId,
            inputData: { tenantId: locker?.tenantId, lockerId, sstLockerId },
          },
        ],
      },
      params: { tenantId: locker?.tenantId },
    }).unwrap()
  }, [
    locker?.tenantId,
    locker?.sstLocker?._id,
    locker?.sst2Locker?.id,
    isV2,
    lockerId,
    createThings,
  ])

  const [sstDrawerOpen, setSstDrawerOpen] = useState(false)

  // ── Delete ──────────────────────────────────────────
  // The confirmation, its mutation and its error state all moved into
  // `SaLockerDeleteDialog` (LOCKER-355). Only the open/closed flag stays here,
  // because the Details panel below is what opens it.
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  if (isCreating) {
    return (
      <SaLayout selectedMenuItemLabelKey="lockers">
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 960 }}>
          <SaLockerForm locker={undefined} isCreating={true} />
        </Box>
      </SaLayout>
    )
  }

  if (!locker?.id) {
    // Gate the skeleton on `isFetching`, NOT `isLoading`. RTK computes
    // `isLoading` as "first load AND no data in the cache entry", so on any
    // cache-warm remount — back/forward, or revisiting the same locker inside
    // `keepUnusedDataFor`'s 60s — the mount clear above empties `saList` while
    // `isLoading` is already false, and this guard would report a locker that
    // exists as "not found". `isFetching` is true for a refetch too, which is
    // exactly the state we are in. A failed fetch gets its own branch, so the
    // false "not found" can no longer become permanent.
    return (
      <SaLayout selectedMenuItemLabelKey="lockers">
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {isLoading || isFetching ? (
            <Stack direction="column" spacing={2}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
            </Stack>
          ) : isError ? (
            <Alert
              severity="error"
              variant="outlined"
              action={
                <Button size="small" onClick={_refetch}>
                  {t("retry")}
                </Button>
              }
            >
              {t("saLockerConsole.loadFailed")}
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              {t("saLockerConsole.notFound")}
            </Alert>
          )}
        </Box>
      </SaLayout>
    )
  }

  const lockerName = lockerDisplay?.i18n?.name || lockerId || ""

  return (
    <SaLayout selectedMenuItemLabelKey="lockers">
      <Stack
        direction="column"
        spacing={3}
        sx={{ p: { xs: 2, sm: 3 }, pl: { sm: 4 }, mt: 1 }}
      >
        <SaLockerConsoleHeader
          name={lockerName}
          lockerId={locker.id}
          tenantName={tenantData?.tenant?.name}
          tenantId={tenantData?.tenant?.id}
          isLoadingTenant={isLoadingTenant}
          vitals={vitals!}
          isVirtual={locker.isVirtual}
          onSync={onRefresh}
          isSyncing={isCreatingThings}
        />

        {/* Configuration and the physical unit as equal peers. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <SaLockerSection ui="UI-SL2W-144"
            title={t("saLockerConsole.configuration")}
            action={
              <Tooltip title={t("refresh")}>
                <IconButton onClick={_refetch} size="small">
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <SaLockerForm locker={locker} isCreating={false} />
          </SaLockerSection>

          <SaLockerSection ui="UI-SL2W-146" title={t("saLockerConsole.unit")}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, height: "100%" }}>
              <SaLockerDashboardView
                locker={locker}
                name={lockerName}
                onRefresh={onRefresh}
                isRefreshing={isCreatingThings}
              />
            </Paper>
          </SaLockerSection>
        </Box>

        {/* Above the migration section on purpose: this is the reversible,
            migration-agnostic control, and an operator mid-incident should reach
            it before the one that rewrites migration state. */}
        <SaLockerSection ui="UI-SL2W-150"
          title={t("saLockerConsole.service")}
          hint={t("saLockerConsole.serviceHint")}
        >
          <SaLockerServiceToggle locker={locker} />
        </SaLockerSection>

        <SaLockerSection ui="UI-SL2W-152"
          title={t("saLockerConsole.migration")}
          hint={t("saLockerConsole.migrationHint")}
        >
          <SaLockerMigrationOverride locker={locker} />
        </SaLockerSection>

        <SaLockerSection ui="UI-SL2W-154" title={t("saLockerConsole.details")}>
          <SaLockerDetailsPanel
            locker={locker}
            vitals={vitals!}
            onOpenSnapshot={() => setSstDrawerOpen(true)}
            onDelete={() => setIsConfirmingDelete(true)}
          />
        </SaLockerSection>

        <Box>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={0.5} justifyContent="space-between">
            <Stack direction="row" spacing={3} flexWrap="wrap" rowGap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t("createdAt")}: {moment(locker.createdAt).format("YYYY-MM-DD HH:mm")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("updatedAt")}: {moment(locker.updatedAt).format("YYYY-MM-DD HH:mm")}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {locker.id}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <SaLockerSnapshotDrawer
        open={sstDrawerOpen}
        onClose={() => setSstDrawerOpen(false)}
        isV2={isV2}
        locker={locker}
        onRefresh={onRefresh}
        isRefreshing={isCreatingThings}
      />

      <SaLockerDeleteDialog
        open={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        lockerId={lockerId}
        tenantId={locker?.tenantId}
      />
    </SaLayout>
  )
}

export default SaLockerPage
